/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React, { useEffect, useMemo } from "react";

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import "../../Styles/styles-ResultsTable.css";
import "../../Styles/styles-Example.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  MAX_VELO_METRICS,
	TOTAL_DISTANCE_METRICS,
  getMetricsToRender
} from "../../Utils/chart_utils.jsx";
import Slider from "react-slick";
import { LoadingComponent } from "../Common/LoadingComponent";
import BarChartHighcharts from "../Charts/BarChart.jsx";
import OptionalFeaturesForCharts from "../Charts/OptionalFeaturesForCharts.jsx";
import {mapDataForHighchartsGroupedBar} from "../../mappers/model/GroupedBarChartMapper.js";
import { mapDataForHighcharts, mapDataForMaxVeloChart } from "../../mappers/model/BarChartMapper.js";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper.js";
import { mapDataForBarAndBubbleChart } from "../../mappers/model/BarAndBubble.js";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../Utils/date_utils.js";


var settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

export const customLabel =
  (key) =>
  ({ rest, travel, ...data }) => {
    if (rest && travel) return "Rest+Travel";
    if (rest) return "Rest";
    if (travel) return "Travel";
    return data[key];
  };

export const getRestTravelBarColor = (training) => {
  if (training?.rest && training?.travel) return "#722ed1";
  if (training?.rest) return "#87d068";
  if (training?.travel) return "#f56a00";
  return "#808080";
};

export const getRestAndTravelDaysInChart = (
  startDate,
  endDate,
  data,
  restAndTravel,
  showRest,
  showTravel
) => {
  if (!data) return [];
  const datesWithData = data.reduce((acc, item) => {
    acc[item.date] = true;
    return acc;
  }, {});
  let result = [];
  for (
    let date = dayjs(startDate);
    date <= dayjs(endDate);
    date = date.add(1, "day")
  ) {
    const dateString = date.format(DATE_FORMAT);
    if (
      (!datesWithData || !datesWithData[dateString]) &&
      restAndTravel &&
      restAndTravel[dateString]
    ) {
      const parsed = {
        date: dateString,
        rest: restAndTravel[dateString] >= 2,
        travel: restAndTravel[dateString] % 2 === 1,
      };
      if ((showRest && parsed.rest) || (showTravel && parsed.travel)) {
        result.push({
          ...parsed,
          rest: parsed.rest && showRest,
          travel: parsed.travel && showTravel,
        });
      }
    }
  }
  return result;
};

export default function TeamAvgCharts({
  data_input,
  player_data,
  home_page,
  team_name,
  showPredictions,
  loading,
  showTravel,
  showRest,
  showACWR,
  dataColorUtil
}) {
  const {
    info: { display_name: teamName, metric: teamMetric, restAndTravel, team_colors: teamColors, custom_charts: customChartData,
      acwr_threshold_config: acwrThresholdConfig
    },
    filter: { startDate, endDate, showWeekly, position, player_group, session, showGameDays },
		teamCharts,
    games,
  } = useSelector(({ team }) => team);
  const [dataIn, setData] = React.useState(data_input);

  useEffect(() => {
    setData(data_input);
  }, [showPredictions, data_input]);

  const data = dataIn?.["data"];
  const restAndTravelDates = getRestAndTravelDaysInChart(
    startDate,
    endDate,
    data,
    restAndTravel,
    showRest,
    showTravel
  );

  let charts = {};

  const enableDrillDown = useMemo(() => {
    return (showGameDays || showWeekly || showACWR || showPredictions || showRest || showTravel) ? false : true
  }, [showGameDays, showWeekly, showACWR, showPredictions, showRest, showTravel])

  let key = "distance",
    maxValue = 0,
    maxPreValue = 0;
  let modifiedChartData = data.map((item, index) => {
    let currVal = data[index][key];
    if (currVal > maxValue) maxValue = currVal;
    if (data[index][key + "_pre"] > maxPreValue)
      maxPreValue = data[index][key + "_pre"];
    if (index < 2) {
      return { ...item };
    } else {
      return {
        ...item,
        [key + "_rolling_average"]:
          (currVal + data[index - 1][key] + data[index - 2][key]) / 3,
      };
    }
  });

  for (let i = 0; i < dataIn.metrics.length; i++) {
		let metric;
		let wellness;
		if (i < dataIn["metrics"].length) {
			metric = dataIn["metrics"][i];
			wellness = false;
		}

		let key = metric.key,
			maxValue = 0;

		data.map((item, index) => {
			let currVal = data[index][key];
      let per_minute_value = 0;
			if (currVal > maxValue) maxValue = currVal;

      if (item[key + "_per_minute"]) {
        per_minute_value = +item[key + "_per_minute"];
      } else {
        per_minute_value = (+item["total_time"] > 0 ? +(+item[key]/ +item["total_time"]).toFixed(2) : 0)
      }
			const itemWithPerMin = {
				...item,
				[key + "_per_minute"]: per_minute_value,
			};

			dataIn.data[index][key + "_per_minute"] = per_minute_value;

			if (wellness === true || index < 2) {
				return { ...itemWithPerMin };
			} else {
				return {
					...itemWithPerMin,
					[key + "_rolling_average"]:
						(currVal +
							data[index - 1][key] +
							data[index - 2][key]) /
						3,
				};
			}
		});
	}

  modifiedChartData = modifiedChartData.concat(
    restAndTravelDates.map((dateData) => ({
      ...dateData,
      disp_date: dayjs(dateData.date).format("MM-DD"),
      [key + "_rt"]: showPredictions
        ? Math.max(maxValue, maxPreValue)
        : maxValue,
    }))
  );
  modifiedChartData.sort((a, b) => {
    return a.date < b.date ? -1 : 1;
  });


  const getMappingFunction = (metric) => {
    let customChart = null;
    let chartType = null;
    if(metric?.customChartData) {
      customChart = metric?.customChartData
      chartType = metric?.customChartData?.type
    }

    switch (chartType) {
      case 'bar_and_bubble_chart': {
        return mapDataForBarAndBubbleChart({
          dataIn,
          mainSeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart.bar_metric),
          secondarySeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart.bubble_metric),
          showACWR,
          showRest,
          showTravel,
          restAndTravel,
          startDate,
          endDate,
          teamName: team_name,
          showPredictions,
          games,
          teamColors: teamColors.all,
          showAvgPlotline: true,
          position,
          playerGroup: player_group,
          session,
          acwrThresholdConfig,
          customTitle: customChart?.title,
          enableDrillDown,
          dataColorUtil
        });
      }
      case 'grouped_bar_chart': {
        return mapDataForHighchartsGroupedBar({
          dataIn,
          games,
          metrics: dataIn.metrics.filter( (metric) => metric.key === customChart.bar1_metric || metric.key === customChart.bar2_metric),
          teamColors: teamColors.all,
          isComparison: false,
          showACWR,
          showRest,
          showTravel,
          restAndTravel,
          startDate,
          endDate,
          showPredictions,
          isTeamChart: false,
          isMetricComparisonChart: true,
          title: customChart?.title,
          enableDrillDown,
          dataColorUtil,
          position,
          playerGroup: player_group,
          session,
        });
      }
      case 'stacked_bar_chart': {
        return stackedBarChartDtoToModel({
          data: dataIn?.data,
          showRest,
          showTravel,
          restAndTravel,
          startDate,
          endDate,
          chartType: customChart?.stack_type,
          showPredictions,
          colors_100: dataIn?.colors,
          games,
          teamName,
          position,
          playerGroup: player_group,
          session,
          showACWR: customChart?.stack_type === 'distance' ? false : true,
          customChartConfig: customChart,
          metrics: dataIn?.metrics,
          acwrThresholdConfig,
          enableDrillDown
        });
      }
      default: {
        // custom chart type either not provided or not recognized.
        // Will get handled by the code below
      }
    }

    if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric.key)) {
      return mapDataForMaxVeloChart({
        dataIn,
        metric,
        showACWR,
        showRest,
        showTravel,
        restAndTravel,
        startDate,
        endDate,
        teamName: team_name,
        showPredictions,
        games,
        teamColors: teamColors.all,
        acwrThresholdConfig,
        enableDrillDown,
        dataColorUtil,
        position,
        playerGroup: player_group,
        session,
      })
    }

    return mapDataForHighcharts({
      dataIn,
      metric,
      showACWR,
      showRest,
      showTravel,
      restAndTravel,
      startDate,
      endDate,
      teamName: team_name,
      showPredictions,
      showAvgPlotline: true,
      games,
      teamColors: teamColors.all,
      session,
      playerGroup: player_group,
      position,
      teamMetric,
      acwrThresholdConfig,
      enableDrillDown,
      dataColorUtil
    });
  }
 
  const metricsToRender = getMetricsToRender(team_name, dataIn?.metrics, customChartData?.chart_types);

  const renderPrimaryCharts = () => {
    if (!(dataIn?.metrics?.length && customChartData?.chart_types && metricsToRender?.length)) {
      return null;
    }

    return metricsToRender
      .map((metric, index) => (
        <li key={`chart-${metric?.key + `${index}` || index}`} className="example-table-graph">
          <OptionalFeaturesForCharts
            chartType={'bar'}
            showAvgPlotline={true}
            optionalFeatures={{showTrendlines: "Trendline" }}
            optionsBuilder={(features) => {
              const opts = getMappingFunction(metric, features);
              return {
                ...opts,
                chart: {
                  ...opts.chart,
                  height: '100%'
                }
              };
            }}
          />
        </li>
      ));
  };

  return (
    <LoadingComponent loading={loading}>
    <div className="outer-example-grid">
      <ul className="list-unstyled example-table-grid" style={{ gap: '3rem' }}>
        {renderPrimaryCharts()}
      </ul>
    </div>
  </LoadingComponent>
  );
}
